import React, { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { getRoutesBasedOnRoles } from "../router/helpers";
import ROUTER_CONFIG from "../router/routesConfig";
import ROLE_ACCESS_RIGHTS, { ROLE_ANONYMOUS } from "../router/roles";
import Menus from "../menus/Menus";
import { useTiaAuth } from "@tia/authenticator";
import {
  setAgentType,
  _getAgentConfigurations as getAgentConfigurations,
  setAgentFirms,
} from "../state/api/configurations/actionCreators";
import "./App.scss";
import "./tailwind.css";
import { I18nextProvider, withTranslation } from "react-i18next";
import { apiConnector } from "../utils/apiConnector";
import { AgentAuthProvider } from "../components/AgentAuthProvider/AgentAuthProvider";
import { State } from "../configureStore";
import { useAtom } from "jotai";
import { i18n } from "../i18n";
import { RESET } from "jotai/utils";
import { lastVisitedAtom, accessTokenAtom, idTokenAtom, featureFlagsChangeAtom, agentFirmIdAtom } from "../atoms/atoms";
import { getAgentFirm } from "../utils/idToken";
import { useModal } from "../hooks/useModal";
import { AgencySwitchModal, AgentFirms } from "../components/Modal/AgencySwitchModal";
import { showMultipleToasts } from "../utils/apiSetup";
import { getGlobalConfiguration } from "../utils/getGlobalConfiguration";
import { ConfigProvider, App as AntdApp } from "antd";
import { NotificationProvider } from "../utils/notificationContext";

interface Props {
  agentConfigurationLoaded: boolean;
  agentConfigurationLoading: boolean;
  getAgentConfigurations: () => void;
  setAgentType: (agentType: string) => void;
  setAgentFirms: (agentFirms: AgentFirms[]) => void;
  agencies: AgentFirms[];
}

const App = (props: Props) => {
  const {
    agentConfigurationLoaded,
    agentConfigurationLoading,
    getAgentConfigurations,
    setAgentType,
    setAgentFirms,
    agencies,
  } = props;
  const location = useLocation();
  const [flag, setFlag] = useState(false);
  const auth = useTiaAuth();
  const [loggedIn, setLoggedIn] = useState(false);
  const [anonymousRoutes, setAnonymousRoutes] = useState<JSX.Element[]>([]);
  const [rolesRoutes, setRolesRoutes] = useState<JSX.Element[]>([]);
  const [lastVisited, setLastVisited] = useAtom(lastVisitedAtom);
  const [accessToken] = useAtom(accessTokenAtom);
  const [idToken] = useAtom(idTokenAtom);
  const [featureFlagValues, setFeatureFlagValues] = useAtom(featureFlagsChangeAtom);
  const [agentFirmId, setAgentFirmId] = useAtom(agentFirmIdAtom);
  const { openModal: openAgencySwitchModal, closeModal: closeAgencySwitchModal } = useModal("agencySwitchAuth");
  const [isAgencySwitching, setIsAgencySwitching] = useState(false);
  const isAgentFirmIdSet = agentFirmId && agentFirmId !== "";

  /* TODO: Domantas
     Remove this in the future, since widgets should handle styling on their own. We need an attribute on 
     Agent portal's body because claim's datepicker is rendered outside the widget body.
     DSI-6752 is the task to fix this.
  */
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments.at(-1) || "";

    if (lastSegment === "new-claim") {
      document.body.dataset.route = lastSegment;
    }

    return () => {
      delete document.body.dataset.route;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (anonymousRoutes.length === 0) {
      setAnonymousRoutes(
        ROLE_ACCESS_RIGHTS[ROLE_ANONYMOUS].map((route) => (
          <Route
            key={ROUTER_CONFIG[route].path}
            path={ROUTER_CONFIG[route].path}
            component={ROUTER_CONFIG[route].component}
          />
        )),
      );
    }
  }, [anonymousRoutes]);

  useEffect(() => {
    if (rolesRoutes.length === 0 && loggedIn) {
      const routes = getRoutesBasedOnRoles();

      if (routes.length === 0) {
        return;
      }

      routes.sort((a, b) => Number(b.exact) - Number(a.exact));

      setRolesRoutes(
        routes.map((route) => (
          <Route key={route.path} path={route.path} component={route.component} exact={route.exact} />
        )),
      );
    }
  }, [rolesRoutes, loggedIn]);

  //accessToken atom should be used, so that "onMount" callback function would be executed later
  //this way it initializes the atom's state from local storage
  useEffect(() => {
    const fetchData = async () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const accessTokenValue = accessToken;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const idTokenValue = idToken;
    };

    fetchData();
  }, []);

  useEffect(() => {
    (async () => {
      const authenticated = await auth.authClient.isAuthenticated();
      setLoggedIn(authenticated);
    })();
  }, []);

  const agentFirmIdFromToken = useMemo(() => getAgentFirm(), [loggedIn]);

  useEffect(() => {
    const setFeatureFlags = async () => {
      const response = await apiConnector().utility.getAgentType(!isAgentFirmIdSet && agentFirmIdFromToken);

      if (response) {
        setAgentType(response.data);
        if (window.featureFlagInternal && response.data === "internal") {
          window.featureFlag = window.featureFlagInternal;
        } else if (window.featureFlagExternal && response.data === "external") {
          window.featureFlag = window.featureFlagExternal;
        }
        setFeatureFlagValues(window.featureFlag);
      }
      setFlag(!flag);
      delete window.featureFlagExternal;
      delete window.featureFlagInternal;
    };

    const setAgencies = async () => {
      if (agentFirmIdFromToken || agencies) {
        return;
      }

      const response = await apiConnector().utility.getAgentFirms(!isAgentFirmIdSet && agentFirmIdFromToken);
      const { error, message, data } = response;
      if (data?.length) {
        setAgentFirms(response.data);
      } else {
        showMultipleToasts([...(error || []), ...(message || [])]);
        setTimeout(() => auth.logout(), getGlobalConfiguration("notification.duration.error") * 1000);
      }
    };

    const setConfigurations = async () => {
      if (agentFirmId) {
        getAgentConfigurations();
        await setFeatureFlags();
      }

      await setAgencies();
    };

    if (loggedIn && !agentConfigurationLoaded && !agentConfigurationLoading) {
      setConfigurations();
    }
  }, [agentConfigurationLoaded, agentConfigurationLoading, loggedIn, agentFirmId]);

  useEffect(() => {
    if (loggedIn && agentFirmIdFromToken && accessToken) {
      setAgentFirmId(agentFirmIdFromToken);
    }
    if (!agentFirmIdFromToken && !agentFirmId) {
      if (accessToken) {
        if (agencies?.length === 1) {
          setAgentFirmId(agencies[0].firmId);
        } else if (agencies?.length > 1) {
          openAgencySwitchModal();
        }
      }
    }
    if (agentFirmId) {
      closeAgencySwitchModal();
    }
  }, [agentFirmId, featureFlagValues, agentFirmIdFromToken, loggedIn, agencies, accessToken]);

  const loggedInContent = () => {
    if (loggedIn && agentFirmId) {
      if (`${location.pathname}${location.search}` === lastVisited) {
        setLastVisited(RESET);
      }

      return (
        <Menus rerender={flag}>
          <Switch>{rolesRoutes}</Switch>
        </Menus>
      );
    }

    return null;
  };

  const AnonymousPaths = () => <Switch>{anonymousRoutes}</Switch>;

  return (
    <ConfigProvider
      theme={{
        hashed: false,
        token: {
          colorPrimary: "#007a84",
          colorLink: "#007a84",
          borderRadius: 4,
          fontFamily: "Muli, sans-serif",
          fontSize: 14,
          controlItemBgHover: "#E6F7FF",
        },
        components: {
          Layout: {
            headerBg: "#FFF",
            headerHeight: 74,
            bodyBg: "#f6fafa",
            siderBg: "#fff",
            triggerBg: "#002140",
            triggerColor: "#fff",
          },
          Card: {
            paddingLG: 15,
          },
          Menu: {
            itemColor: "#81849a",
            iconSize: 14,
            itemHoverBg: "#f5f7f7",
            itemActiveBg: "#f5f7f7",
          },
          Breadcrumb: {
            linkColor: "#a5a6ac",
            linkHoverColor: "#4e505e",
            lastItemColor: "#4e505e",
          },
          Table: {
            rowHoverBg: "#f5f7f7",
          },
        },
      }}
    >
      <AntdApp notification={{ stack: false, showProgress: true }}>
        <NotificationProvider>
          <Fragment>
            <AnonymousPaths />
            {agentFirmId && !isAgencySwitching && loggedInContent()}
            {agencies?.length > 1 && (
              <AgencySwitchModal
                name="agencySwitchAuth"
                agencies={agencies}
                closable={false}
                isAgencySwitching={isAgencySwitching}
                setIsAgencySwitching={setIsAgencySwitching}
              ></AgencySwitchModal>
            )}
          </Fragment>
        </NotificationProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

const mapStateToProps = (state: State) => ({
  agentConfigurationLoading: state.api.configurations.agentConfigurations.isFetching,
  agentConfigurationLoaded: state.api.configurations.agentConfigurations.isLoaded,
  agencies: state.api.configurations.agentFirms.data,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getAgentConfigurations,
      setAgentType,
      setAgentFirms,
    },
    dispatch,
  );

const AppComponent = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(App));

const SuspenseApp = () => {
  return (
    <Suspense fallback="loading">
      <I18nextProvider i18n={i18n}>
        <AgentAuthProvider>
          <AppComponent />
        </AgentAuthProvider>
      </I18nextProvider>
    </Suspense>
  );
};
export default SuspenseApp;
